import { FormattedMessage } from 'react-intl';

import { FILTERS, FilterWidget } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import ShowDetails from '@sorare/marketplace/src/components/TokenPreview/ShowDetails';
import ConnectedRangeSlider from '@sorare/marketplace/src/search/RangeSlider';
import {
  AcceptedCurrencyGroup,
  NotInLineupFilter,
  PriceGroup,
  PromotionalEventFilter,
  RarityGroup,
  RefineAppearances,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineCompetitionEligibility,
  RefineInSeason,
  RefineNationality,
  RefineOnSale,
  RefinePlayer,
  RefineSerialNumber,
  filterSeparator,
} from '@sorare/marketplace/src/searchCards';
import { makeRangeFilter } from '@sorare/marketplace/src/searchCards/RangeSlider';
import {
  RefineActiveClub,
  RefineClub,
} from '@sorare/marketplace/src/searchCards/RefineClubs';
import { RefineSingleYearSeason } from '@sorare/marketplace/src/searchCards/RefineList';
import { MyAuctionsGroup } from '@sorare/marketplace/src/searchCards/refineGroups';

import BaseballCustomDecksFilter from 'components/deck/BaseballCustomDecksFilter';

import RefineMLBPosition from './RefineMLBPosition';
import RefineProbablePitchers from './RefineProbablePitchers';

export const RefineLastFifteenAverageScore = makeRangeFilter(({ visible }) => {
  return (
    <ConnectedRangeSlider
      attribute={FILTERS.mlbLastFifteenAverageScore.attribute}
      visible={visible}
    />
  );
}, FILTERS.mlbLastFifteenAverageScore);

export const RefineSeasonAverageScore = makeRangeFilter(({ visible }) => {
  return (
    <ConnectedRangeSlider
      attribute={FILTERS.mlbSeasonAverageScore.attribute}
      visible={visible}
    />
  );
}, FILTERS.mlbSeasonAverageScore);

const InSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: RefineInSeason,
  filters: [],
};

const CardGroup = {
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    RefineCompetitionEligibility,
    RefineMLBPosition,
    RefineSingleYearSeason,
    RefineClub,
  ],
};

export const MatchInfoGroup = (
  withSeasonScore: boolean,
  props?: {
    withoutProbablePitchers?: boolean;
  }
) => ({
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    withSeasonScore ? RefineSeasonAverageScore : RefineLastFifteenAverageScore,
    RefineAppearances(),
    ...((props?.withoutProbablePitchers && []) || [RefineProbablePitchers]),
  ],
});

const PromotedCardsGroup = {
  slug: 'toggle',
  title: null,
  filters: [PromotionalEventFilter],
};

const PlayerGroup = (props?: { withoutActiveClub?: boolean }) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefineCardPlayerAge,
    RefineNationality,
    RefinePlayer,
    ...((props?.withoutActiveClub && []) || [RefineActiveClub]),
  ],
});

const CollectibleGroup = (props?: { withoutCardLevel?: boolean }) => ({
  slug: 'collectibles',
  title: <FormattedMessage {...cardAttributes.collectibles} />,
  filters: [
    RefineSerialNumber,
    ...((props?.withoutCardLevel && []) || [RefineCardLevel]),
    RefineCardEdition,
  ],
});

const ToggleGroup = (filters: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    ...(filters.flatMap((filter, i) => [
      ...((i && [filterSeparator]) || []),
      filter,
    ]) || []),
  ],
});

export const primaryMarketFilters = (withSeasonScore: boolean) => [
  MyAuctionsGroup,
  RarityGroup,
  PromotedCardsGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore),
  PlayerGroup(),
  CollectibleGroup({ withoutCardLevel: true }),
];

export const instantBuyMarketFilters = (withSeasonScore: boolean) => [
  RarityGroup,
  PromotedCardsGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore, { withoutProbablePitchers: true }),
  PlayerGroup(),
  CollectibleGroup({ withoutCardLevel: true }),
];

export const secondaryMarketFilters = (withSeasonScore: boolean) =>
  [
    InSeasonFilter,
    RarityGroup,
    PriceGroup,
    CardGroup,
    MatchInfoGroup(withSeasonScore),
    PlayerGroup(),
    CollectibleGroup(),
    AcceptedCurrencyGroup,
  ].filter(Boolean);

export const secondaryMarketStackedFilters = [
  PriceGroup,
  CardGroup,
  CollectibleGroup(),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = (withSeasonScore: boolean) => [
  InSeasonFilter,
  ToggleGroup([RefineOnSale()]),
  RarityGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore, { withoutProbablePitchers: true }),
  CollectibleGroup(),
  AcceptedCurrencyGroup,
];

export const clubPageFilters = (withSeasonScore: boolean) => [
  InSeasonFilter,
  ToggleGroup([RefineOnSale()]),
  RarityGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore),
  PlayerGroup({ withoutActiveClub: false }),
  CollectibleGroup(),
  AcceptedCurrencyGroup,
];

export const myClubFilters = (
  withSeasonScore: boolean,
  showDecksFilter: boolean
) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    InSeasonFilter,
    ToggleGroup([RefineOnSale(), NotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'decks', filters: [BaseballCustomDecksFilter] },
    ]) ||
      []),
    RarityGroup,
    CardGroup,
    MatchInfoGroup(withSeasonScore),
    PlayerGroup(),
    CollectibleGroup(),
    AcceptedCurrencyGroup,
  ].filter(Boolean);
